import { useEffect, useRef, useState } from 'react';
import { PlaneFinderApp } from '../app/PlaneFinderApp';
import { usePlaneFinder } from './usePlaneFinder';
import { AboutPanel } from './AboutPanel';
import { DropPanel } from './DropPanel';
import { Hud } from './Hud';
import { DimsOverlay } from './DimsOverlay';
import { RectOverlay } from './RectOverlay';
import { ControlPanel } from './ControlPanel';
import { PreparingOverlay } from './PreparingOverlay';

// Auto-loaded demo model (bundled into the site build; see vite.config.ts).
// Library embeds can pass `demoModelUrl={null}` to disable, or their own URL.
const SAMPLE_MODEL = '/sample.glb';

/**
 * Harness shell, styled after the transcoder harness: a top bar, a scrollable
 * left column (About + file loader, then the capture panel), and the Babylon 3D
 * view filling the remaining space. The 3D view + its overlays are canvas-relative,
 * so picking and the rect overlay stay correct in the sub-region.
 */
export function PlaneFinderViewer({ demoModelUrl = SAMPLE_MODEL }: { demoModelUrl?: string | null } = {}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [app, setApp] = useState<PlaneFinderApp | null>(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    const instance = new PlaneFinderApp(canvasRef.current);
    setApp(instance);
    (window as unknown as { planeFinderApp?: PlaneFinderApp }).planeFinderApp = instance;

    // Lazy-load the demo: defer past first paint so the model fetch + precompute
    // don't block initial interactivity. The page lands instantly (About + drop);
    // the demo appears a moment later. `cancelled` guards an unmount race, and
    // hasRequestedLoad guards a user who dropped their own GLB within the idle
    // window — the demo must never queue behind (and then replace) their model.
    let cancelled = false;
    const loadDemo = () => {
      if (!cancelled && demoModelUrl && !instance.hasRequestedLoad) {
        void instance.loadModel(demoModelUrl, 'sample.glb').catch(() => {});
      }
    };
    const idle = typeof requestIdleCallback === 'function';
    const handle = idle ? requestIdleCallback(loadDemo, { timeout: 1500 }) : window.setTimeout(loadDemo, 800);

    return () => {
      cancelled = true;
      if (idle) cancelIdleCallback(handle as number);
      else window.clearTimeout(handle as number);
      const w = window as unknown as { planeFinderApp?: PlaneFinderApp };
      if (w.planeFinderApp === instance) delete w.planeFinderApp; // no dangling disposed app
      instance.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- demoModelUrl is a mount-time choice
  }, []);

  // Download the captured PNG immediately; the filename carries the mm dimensions.
  useEffect(() => {
    if (!app) return;
    return app.events.on('capture-complete', (result) => {
      const wmm = Math.round(result.worldWidth * 1000);
      const hmm = Math.round(result.worldHeight * 1000);
      const url = URL.createObjectURL(result.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `plane-finder-${wmm}x${hmm}mm.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    });
  }, [app]);

  const state = usePlaneFinder(app);
  const preparing = state.stage === 'view' && !state.analysisReady;

  return (
    <div className="app">
      <header className="hdr">
        <div className="brand">
          <span className="mark">P</span>
          <span className="crumbs">
            <span className="c-strong">Plane Finder</span>
            <span className="sep">/</span>
            <span className="c-dim">Flat-surface capture</span>
          </span>
        </div>
        <span className="spacer" />
      </header>

      <div className="body">
        <aside className="col pf-sidebar">
          {app && <ControlPanel app={app} state={state} />}
          {app && <DropPanel app={app} />}
          <AboutPanel />
        </aside>

        <div className="pf-view">
          <canvas id="renderCanvas" ref={canvasRef} className="ej-planefinder-canvas" />
          {app && (
            <>
              <Hud state={state} />
              <DimsOverlay region={state.region} />
              <RectOverlay app={app} active={state.stage === 'framing'} />
              {preparing && <PreparingOverlay />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
